import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useTranslate, useNotify, useDataProvider, Confirm } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import TurnstileKeysDialog from './TurnstileKeysDialog';
import {
    Box,
    Alert,
    Button,
    Card,
    CardContent,
    Checkbox,
    Divider,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
    CircularProgress,
} from '@mui/material';

const DEFAULT_INVITATION_TTL_DAYS = 30;
const DEFAULT_USER_DATA_RECHECK_DAYS = 248;
const DEFAULT_SCHEDULE = [3, 10, 30];
const AUTO_RESEND_MODES = ['all', 'stuck_failed'];
const TURNSTILE_DEV_SCENARIOS = [
    'normal',
    'always_pass',
    'always_fail',
    'server_failure',
    'duplicate_token',
    'interactive',
    'client_error',
    'token_expired',
    'pending',
    'missing_token',
    'siteverify_unreachable',
];
const DEFAULT_LICENSE_CACHE_TTL_SECONDS = 120;
const DEFAULT_TEACHER_CACHE_TTL_SECONDS = 86400;
const TARGET_ENVS = [
    { value: 'prod', label: 'Tuotanto' },
    { value: 'dev', label: 'Dev' },
    { value: 'next', label: 'Next' },
];
const DIAGNOSTIC_BACKENDS = [
    'kumppani',
    'opettaja-nova',
    'oppilas-nova',
    'opiskelija-nova',
    'integraatio',
    'oidc-license',
    'pilotit',
    'oidc',
    'infra',
];

const parseSchedule = (input: string): number[] | null => {
    const parts = input
        .split(',')
        .map(s => s.trim())
        .filter(s => s !== '')
        .map(Number);
    if (
        parts.length < 1 ||
        parts.length > 20 ||
        parts.some(n => !Number.isInteger(n) || n < 1 || n > 365)
    )
        return null;
    return parts;
};

// Admin settings: list-view session toggle + all invitation-related settings.
const SettingsPanel = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const dataProvider = useDataProvider();
    const { canAccess: canReadSecrets } = useCanAccess({
        action: 'read',
        resource: 'admin/secrets',
    });
    const { canAccess: canWriteSecrets } = useCanAccess({
        action: 'write',
        resource: 'admin/secrets',
    });
    const [turnstileKeysOpen, setTurnstileKeysOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [envTab, setEnvTab] = useState('');
    const [currentEnv, setCurrentEnv] = useState('');
    const [saveDefaultsConfirmOpen, setSaveDefaultsConfirmOpen] =
        useState(false);
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
    const [turnstileDisableConfirmOpen, setTurnstileDisableConfirmOpen] =
        useState(false);

    const [turnstileEnabled, setTurnstileEnabled] = useState(true);
    const [turnstileDevScenario, setTurnstileDevScenario] = useState('normal');
    const [sessionsEnabled, setSessionsEnabled] = useState(true);
    const [campaignEmail, setCampaignEmail] = useState('');
    const [staleInfoEnabled, setStaleInfoEnabled] = useState(true);
    const [basicLoginEnabled, setBasicLoginEnabled] = useState(false);
    const [ttlDays, setTtlDays] = useState<number>(DEFAULT_INVITATION_TTL_DAYS);
    const [ttlInput, setTtlInput] = useState<string>(
        String(DEFAULT_INVITATION_TTL_DAYS)
    );
    const [ttlEnforced, setTtlEnforced] = useState(false);

    const [userDataRecheckDays, setUserDataRecheckDays] = useState<number>(
        DEFAULT_USER_DATA_RECHECK_DAYS
    );
    const [userDataRecheckDaysInput, setUserDataRecheckDaysInput] =
        useState<string>(String(DEFAULT_USER_DATA_RECHECK_DAYS));

    const [autoEnabled, setAutoEnabled] = useState(false);
    const [autoMode, setAutoMode] = useState('stuck_failed');
    const [scheduleSaved, setScheduleSaved] =
        useState<number[]>(DEFAULT_SCHEDULE);
    const [scheduleInput, setScheduleInput] = useState(
        DEFAULT_SCHEDULE.join(', ')
    );

    const [licenseCacheTtl, setLicenseCacheTtl] = useState<number>(
        DEFAULT_LICENSE_CACHE_TTL_SECONDS
    );
    const [licenseCacheTtlInput, setLicenseCacheTtlInput] = useState<string>(
        String(DEFAULT_LICENSE_CACHE_TTL_SECONDS)
    );
    const [licenseCachePrewarmEnabled, setLicenseCachePrewarmEnabled] =
        useState(true);
    const [licenseDataCacheEnabled, setLicenseDataCacheEnabled] =
        useState(true);

    const [teacherCacheTtl, setTeacherCacheTtl] = useState<number>(
        DEFAULT_TEACHER_CACHE_TTL_SECONDS
    );
    const [teacherCacheTtlInput, setTeacherCacheTtlInput] = useState<string>(
        String(DEFAULT_TEACHER_CACHE_TTL_SECONDS)
    );
    const [teacherCachePrewarmEnabled, setTeacherCachePrewarmEnabled] =
        useState(true);
    const [teacherDataCacheEnabled, setTeacherDataCacheEnabled] =
        useState(true);
    const [
        teacherCacheInvalidateOnEnabled,
        setTeacherCacheInvalidateOnEnabled,
    ] = useState(true);
    const [
        productMemCacheBroadcastEnabled,
        setProductMemCacheBroadcastEnabled,
    ] = useState(true);
    const [diagnosticBackend, setDiagnosticBackend] = useState('kumppani');
    const [globalDiagnosticEnabled, setGlobalDiagnosticEnabled] =
        useState(false);
    const [diagnosticBackends, setDiagnosticBackends] = useState<
        Record<string, any>
    >({});
    const [diagnosticRoutes, setDiagnosticRoutes] = useState<
        Record<
            string,
            Array<{
                path: string;
                method: string;
                operation_id?: string | null;
            }>
        >
    >({});
    const [diagnosticEnabled, setDiagnosticEnabled] = useState(false);
    const [diagnosticPaths, setDiagnosticPaths] = useState('/teacher/{guid}');
    const [diagnosticDetail, setDiagnosticDetail] = useState('basic');
    const [diagnosticExpiresAt, setDiagnosticExpiresAt] = useState('');
    // Muistaa viimeksi valitut yksittäiset polut, jotta "kaikki polut" voidaan kytkeä pois palauttaen ne.
    const previousDiagnosticPathsRef = useRef('');
    const diagnosticAllPathsSelected = diagnosticPaths.trim() === '*';

    const applySettingsData = (d: Record<string, any>) => {
        setCampaignEmail(d.sample_books_csv_email || '');
        setTurnstileEnabled(d.turnstile_enabled !== false);
        setTurnstileDevScenario(
            TURNSTILE_DEV_SCENARIOS.includes(d.turnstile_dev_scenario)
                ? d.turnstile_dev_scenario
                : 'normal'
        );
        setSessionsEnabled(d.list_sessions_enabled !== false);
        setStaleInfoEnabled(d.list_stale_info_enabled !== false);
        setBasicLoginEnabled(d.basic_login_enabled === true);
        const days =
            Number(d.invitation_ttl_days) || DEFAULT_INVITATION_TTL_DAYS;
        setTtlDays(days);
        setTtlInput(String(days));
        setTtlEnforced(d.invitation_ttl_enforced === true);

        const recheckDays =
            Number(d.user_data_recheck_days) || DEFAULT_USER_DATA_RECHECK_DAYS;
        setUserDataRecheckDays(recheckDays);
        setUserDataRecheckDaysInput(String(recheckDays));

        setAutoEnabled(d.invitation_auto_resend_enabled === true);
        setAutoMode(
            AUTO_RESEND_MODES.includes(d.invitation_auto_resend_mode)
                ? d.invitation_auto_resend_mode
                : 'stuck_failed'
        );
        const sched = Array.isArray(d.invitation_auto_resend_schedule_days)
            ? d.invitation_auto_resend_schedule_days
            : DEFAULT_SCHEDULE;
        setScheduleSaved(sched);
        setScheduleInput(sched.join(', '));

        const licenseTtl =
            Number(d.license_cache_ttl_seconds) >= 0
                ? Number(d.license_cache_ttl_seconds)
                : DEFAULT_LICENSE_CACHE_TTL_SECONDS;
        setLicenseCacheTtl(licenseTtl);
        setLicenseCacheTtlInput(String(licenseTtl));
        setLicenseCachePrewarmEnabled(
            d.license_cache_prewarm_enabled !== false
        );
        setLicenseDataCacheEnabled(d.license_data_cache_enabled !== false);

        const teacherTtl =
            Number(d.teacher_cache_ttl_seconds) >= 0
                ? Number(d.teacher_cache_ttl_seconds)
                : DEFAULT_TEACHER_CACHE_TTL_SECONDS;
        setTeacherCacheTtl(teacherTtl);
        setTeacherCacheTtlInput(String(teacherTtl));
        setTeacherCachePrewarmEnabled(
            d.teacher_cache_prewarm_enabled !== false
        );
        setTeacherDataCacheEnabled(d.teacher_data_cache_enabled !== false);
        setTeacherCacheInvalidateOnEnabled(
            d.teacher_cache_invalidate_on_enabled !== false
        );
        setProductMemCacheBroadcastEnabled(
            d.product_mem_cache_broadcast_enabled !== false
        );

        const loadedBackends = d.apm_diagnostic?.backends || {};
        setGlobalDiagnosticEnabled(d.apm_diagnostic?.enabled === true);
        setDiagnosticBackends(loadedBackends);
        setDiagnosticRoutes(d.apm_routes || {});
        const diagnostic = loadedBackends.kumppani || {};
        setDiagnosticEnabled(diagnostic.enabled === true);
        setDiagnosticPaths(
            Array.isArray(diagnostic.paths)
                ? diagnostic.paths.join('\n')
                : '/teacher/{guid}'
        );
        setDiagnosticDetail(
            ['basic', 'diagnostic', 'verbose'].includes(diagnostic.detail)
                ? diagnostic.detail
                : 'basic'
        );
        setDiagnosticExpiresAt(
            typeof diagnostic.expires_at === 'string'
                ? diagnostic.expires_at.slice(0, 16)
                : ''
        );
    };

    const loadSettings = async (env?: string) => {
        setLoading(true);
        try {
            const res = await (dataProvider as any).getAny(
                'admin/settings',
                env ? `env=${env}` : ''
            );
            const d = res?.data || {};
            applySettingsData(d);
            setCurrentEnv(d.current_env || '');
            setEnvTab(env || d.current_env || '');
        } catch (e) {
            notify('hallinta.settings.error', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEnvTabChange = (_: unknown, value: string) => {
        loadSettings(value);
    };

    // Prod saa hallita myös dev/next-ympäristöjen asetuksia (ks. assertCanWriteEnv backendissä).
    const isEditable = envTab === currentEnv || currentEnv === 'prod';

    const saveSettings = async (partial: Record<string, unknown>) => {
        const res = await fetch(
            `${process.env.REACT_APP_INFRA_API}/admin/settings?env=${envTab}`,
            {
                method: 'PUT',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify(partial),
            }
        );
        if (!res.ok) throw new Error('save failed');
        return res.json();
    };

    const withSave = async (
        partial: Record<string, unknown>,
        revert?: () => void
    ) => {
        setSaving(true);
        try {
            const saved = await saveSettings(partial);
            notify('hallinta.settings.saved', { type: 'success' });
            return saved;
        } catch (e) {
            if (revert) revert();
            notify('hallinta.settings.error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const postAdminSettingsAction = async (path: string) => {
        const res = await fetch(
            `${process.env.REACT_APP_INFRA_API}/admin/settings/${path}?env=${envTab}`,
            {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
            }
        );
        if (!res.ok) throw new Error('action failed');
        return res.json();
    };

    const handleSaveAsDefaults = async () => {
        setSaveDefaultsConfirmOpen(false);
        setSaving(true);
        try {
            await postAdminSettingsAction('save-defaults');
            notify('hallinta.settings.defaults_saved', { type: 'success' });
        } catch (e) {
            notify('hallinta.settings.error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleResetToDefaults = async () => {
        setResetConfirmOpen(false);
        setSaving(true);
        try {
            const data = await postAdminSettingsAction('reset');
            applySettingsData(data || {});
            notify('hallinta.settings.reset_done', { type: 'success' });
        } catch (e) {
            notify('hallinta.settings.error', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleToggleSessions = (checked: boolean) => {
        const prev = sessionsEnabled;
        setSessionsEnabled(checked);
        withSave({ list_sessions_enabled: checked }, () =>
            setSessionsEnabled(prev)
        );
    };

    const handleToggleTurnstile = (checked: boolean) => {
        const previous = turnstileEnabled;
        setTurnstileEnabled(checked);
        withSave({ turnstile_enabled: checked }, () =>
            setTurnstileEnabled(previous)
        );
    };

    const handleSaveTurnstileDevScenario = async (scenario: string) => {
        const previous = turnstileDevScenario;
        setTurnstileDevScenario(scenario);
        const saved = await withSave(
            { turnstile_dev_scenario: scenario },
            () => setTurnstileDevScenario(previous)
        );
        if (saved) {
            setTurnstileDevScenario(
                TURNSTILE_DEV_SCENARIOS.includes(saved.turnstile_dev_scenario)
                    ? saved.turnstile_dev_scenario
                    : 'normal'
            );
        }
    };

    const confirmTurnstileDisable = () => {
        setTurnstileDisableConfirmOpen(false);
        handleToggleTurnstile(false);
    };

    const handleToggleStaleInfo = (checked: boolean) => {
        const prev = staleInfoEnabled;
        setStaleInfoEnabled(checked);
        withSave({ list_stale_info_enabled: checked }, () =>
            setStaleInfoEnabled(prev)
        );
    };

    const handleToggleBasicLogin = (checked: boolean) => {
        const prev = basicLoginEnabled;
        setBasicLoginEnabled(checked);
        withSave({ basic_login_enabled: checked }, () =>
            setBasicLoginEnabled(prev)
        );
    };

    const handleSaveTtl = async () => {
        const days = Number(ttlInput);
        if (!Number.isInteger(days) || days < 1 || days > 365) {
            notify('hallinta.settings.invitation_ttl_invalid', {
                type: 'warning',
            });
            return;
        }
        const saved = await withSave({ invitation_ttl_days: days });
        if (saved) {
            const savedDays =
                Number(saved.invitation_ttl_days) ||
                DEFAULT_INVITATION_TTL_DAYS;
            setTtlDays(savedDays);
            setTtlInput(String(savedDays));
        }
    };

    const handleSaveUserDataRecheckDays = async () => {
        const days = Number(userDataRecheckDaysInput);
        if (!Number.isInteger(days) || days < 1 || days > 3650) {
            notify('hallinta.settings.user_data_recheck_days_invalid', {
                type: 'warning',
            });
            return;
        }
        const saved = await withSave({ user_data_recheck_days: days });
        if (saved) {
            const savedDays =
                Number(saved.user_data_recheck_days) ||
                DEFAULT_USER_DATA_RECHECK_DAYS;
            setUserDataRecheckDays(savedDays);
            setUserDataRecheckDaysInput(String(savedDays));
        }
    };

    const handleToggleEnforced = (checked: boolean) => {
        const prev = ttlEnforced;
        setTtlEnforced(checked);
        withSave({ invitation_ttl_enforced: checked }, () =>
            setTtlEnforced(prev)
        );
    };

    const handleToggleAuto = (checked: boolean) => {
        const prev = autoEnabled;
        setAutoEnabled(checked);
        withSave({ invitation_auto_resend_enabled: checked }, () =>
            setAutoEnabled(prev)
        );
    };

    const handleChangeMode = (value: string) => {
        const prev = autoMode;
        setAutoMode(value);
        withSave({ invitation_auto_resend_mode: value }, () =>
            setAutoMode(prev)
        );
    };

    const handleSaveSchedule = async () => {
        const parsed = parseSchedule(scheduleInput);
        if (!parsed) {
            notify('hallinta.settings.auto_resend_schedule_invalid', {
                type: 'warning',
            });
            return;
        }
        const saved = await withSave({
            invitation_auto_resend_schedule_days: parsed,
        });
        if (saved) {
            const s = Array.isArray(saved.invitation_auto_resend_schedule_days)
                ? saved.invitation_auto_resend_schedule_days
                : parsed;
            setScheduleSaved(s);
            setScheduleInput(s.join(', '));
        }
    };

    const handleSaveLicenseCacheTtl = async () => {
        const ttl = Number(licenseCacheTtlInput);
        if (!Number.isInteger(ttl) || ttl < 0 || ttl > 3600) {
            notify('hallinta.settings.license_cache_ttl_invalid', {
                type: 'warning',
            });
            return;
        }
        const saved = await withSave({ license_cache_ttl_seconds: ttl });
        if (saved) {
            const savedTtl =
                Number(saved.license_cache_ttl_seconds) >= 0
                    ? Number(saved.license_cache_ttl_seconds)
                    : DEFAULT_LICENSE_CACHE_TTL_SECONDS;
            setLicenseCacheTtl(savedTtl);
            setLicenseCacheTtlInput(String(savedTtl));
        }
    };

    const handleToggleLicensePrewarm = (checked: boolean) => {
        const prev = licenseCachePrewarmEnabled;
        setLicenseCachePrewarmEnabled(checked);
        withSave({ license_cache_prewarm_enabled: checked }, () =>
            setLicenseCachePrewarmEnabled(prev)
        );
    };

    const handleToggleLicenseDataCache = (checked: boolean) => {
        const prev = licenseDataCacheEnabled;
        setLicenseDataCacheEnabled(checked);
        withSave({ license_data_cache_enabled: checked }, () =>
            setLicenseDataCacheEnabled(prev)
        );
    };

    const handleSaveTeacherCacheTtl = async () => {
        const ttl = Number(teacherCacheTtlInput);
        if (!Number.isInteger(ttl) || ttl < 0 || ttl > 604800) {
            notify('hallinta.settings.teacher_cache_ttl_invalid', {
                type: 'warning',
            });
            return;
        }
        const saved = await withSave({ teacher_cache_ttl_seconds: ttl });
        if (saved) {
            const savedTtl =
                Number(saved.teacher_cache_ttl_seconds) >= 0
                    ? Number(saved.teacher_cache_ttl_seconds)
                    : DEFAULT_TEACHER_CACHE_TTL_SECONDS;
            setTeacherCacheTtl(savedTtl);
            setTeacherCacheTtlInput(String(savedTtl));
        }
    };

    const handleToggleTeacherPrewarm = (checked: boolean) => {
        const prev = teacherCachePrewarmEnabled;
        setTeacherCachePrewarmEnabled(checked);
        withSave({ teacher_cache_prewarm_enabled: checked }, () =>
            setTeacherCachePrewarmEnabled(prev)
        );
    };

    const handleToggleTeacherDataCache = (checked: boolean) => {
        const prev = teacherDataCacheEnabled;
        setTeacherDataCacheEnabled(checked);
        withSave({ teacher_data_cache_enabled: checked }, () =>
            setTeacherDataCacheEnabled(prev)
        );
    };

    const handleToggleTeacherInvalidateOn = (checked: boolean) => {
        const prev = teacherCacheInvalidateOnEnabled;
        setTeacherCacheInvalidateOnEnabled(checked);
        withSave({ teacher_cache_invalidate_on_enabled: checked }, () =>
            setTeacherCacheInvalidateOnEnabled(prev)
        );
    };

    const handleToggleProductMemCacheBroadcast = (checked: boolean) => {
        const prev = productMemCacheBroadcastEnabled;
        setProductMemCacheBroadcastEnabled(checked);
        withSave({ product_mem_cache_broadcast_enabled: checked }, () =>
            setProductMemCacheBroadcastEnabled(prev)
        );
    };

    const handleSaveDiagnostic = async () => {
        const paths = diagnosticPaths
            .split('\n')
            .map(path => path.trim())
            .filter(Boolean);
        const isAllPaths = paths.length === 1 && paths[0] === '*';
        if (
            paths.length === 0 ||
            (!isAllPaths &&
                paths.some(
                    path =>
                        !/^(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD)\s+\//i.test(
                            path
                        ) && !path.startsWith('/')
                ))
        ) {
            notify('hallinta.settings.diagnostic_paths_invalid', {
                type: 'warning',
            });
            return;
        }
        if (isAllPaths && !diagnosticExpiresAt) {
            notify('hallinta.settings.diagnostic_all_paths_requires_expiry', {
                type: 'warning',
            });
            return;
        }
        const nextBackends = {
            ...diagnosticBackends,
            [diagnosticBackend]: {
                enabled: diagnosticEnabled,
                paths,
                detail: diagnosticDetail,
                expires_at: diagnosticExpiresAt
                    ? new Date(diagnosticExpiresAt).toISOString()
                    : null,
            },
        };
        const saved = await withSave({
            apm_diagnostic: {
                enabled: globalDiagnosticEnabled,
                backends: nextBackends,
            },
        });
        if (saved) setDiagnosticBackends(nextBackends);
    };

    const handleToggleGlobalDiagnostic = (checked: boolean) => {
        const prev = globalDiagnosticEnabled;
        setGlobalDiagnosticEnabled(checked);
        withSave(
            {
                apm_diagnostic: {
                    enabled: checked,
                    backends: diagnosticBackends,
                },
            },
            () => setGlobalDiagnosticEnabled(prev)
        );
    };

    const handleDiagnosticBackendChange = (backend: string) => {
        const diagnostic = diagnosticBackends[backend] || {};
        previousDiagnosticPathsRef.current = '';
        setDiagnosticBackend(backend);
        setDiagnosticEnabled(diagnostic.enabled === true);
        setDiagnosticPaths(
            Array.isArray(diagnostic.paths) ? diagnostic.paths.join('\n') : ''
        );
        setDiagnosticDetail(
            ['basic', 'diagnostic', 'verbose'].includes(diagnostic.detail)
                ? diagnostic.detail
                : 'basic'
        );
        setDiagnosticExpiresAt(
            typeof diagnostic.expires_at === 'string'
                ? diagnostic.expires_at.slice(0, 16)
                : ''
        );
    };

    const handleDiagnosticPathChange = (
        path: string,
        checked: boolean,
        legacyPath?: string
    ) => {
        setDiagnosticPaths(current => {
            const paths = new Set(
                current
                    .split('\n')
                    .map(value => value.trim())
                    .filter(Boolean)
            );
            if (checked) paths.add(path);
            else {
                paths.delete(path);
                if (legacyPath) paths.delete(legacyPath);
            }
            return Array.from(paths).join('\n');
        });
    };

    const handleToggleDiagnosticAllPaths = (checked: boolean) => {
        if (checked) {
            previousDiagnosticPathsRef.current = diagnosticPaths;
            setDiagnosticPaths('*');
        } else {
            setDiagnosticPaths(previousDiagnosticPathsRef.current);
        }
    };

    const diagnosticBackendLabel = (backend: string) => {
        const labels: Record<string, string> = {
            kumppani: 'Kumppani',
            'opettaja-nova': 'Opettaja Nova',
            'oppilas-nova': 'Oppilas Nova',
            'opiskelija-nova': 'Opiskelija Nova',
            integraatio: 'Integraatio',
            'oidc-license': 'OIDC License',
            pilotit: 'Pilotit',
            oidc: 'OIDC',
            infra: 'Infra',
        };
        return labels[backend] || backend;
    };

    if (loading)
        return (
            <Box sx={{ p: 3 }}>
                <CircularProgress />
            </Box>
        );

    return (
        <>
            {turnstileKeysOpen && (
                <TurnstileKeysDialog
                    environment={currentEnv}
                    onClose={() => setTurnstileKeysOpen(false)}
                />
            )}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
            >
                <Tabs value={envTab} onChange={handleEnvTabChange}>
                    {TARGET_ENVS.map(e => (
                        <Tab key={e.value} value={e.value} label={e.label} />
                    ))}
                </Tabs>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={saving || !isEditable}
                        onClick={() => setSaveDefaultsConfirmOpen(true)}
                    >
                        {translate('hallinta.settings.save_as_defaults')}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        disabled={saving || !isEditable}
                        onClick={() => setResetConfirmOpen(true)}
                    >
                        {translate('hallinta.settings.reset_to_defaults')}
                    </Button>
                </Stack>
            </Stack>
            {!isEditable && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    {translate('hallinta.settings.read_only_env_notice')}
                </Typography>
            )}
            <Confirm
                isOpen={saveDefaultsConfirmOpen}
                loading={saving}
                title={translate(
                    'hallinta.settings.save_as_defaults_confirm_title'
                )}
                content={translate(
                    'hallinta.settings.save_as_defaults_confirm_content'
                )}
                onConfirm={handleSaveAsDefaults}
                onClose={() => setSaveDefaultsConfirmOpen(false)}
            />
            <Confirm
                isOpen={resetConfirmOpen}
                loading={saving}
                title={translate(
                    'hallinta.settings.reset_to_defaults_confirm_title'
                )}
                content={translate(
                    'hallinta.settings.reset_to_defaults_confirm_content'
                )}
                onConfirm={handleResetToDefaults}
                onClose={() => setResetConfirmOpen(false)}
            />
            <Confirm
                isOpen={turnstileDisableConfirmOpen}
                title={translate(
                    'hallinta.settings.turnstile_disable_confirm_title'
                )}
                content={translate(
                    'hallinta.settings.turnstile_disable_confirm_content'
                )}
                onConfirm={confirmTurnstileDisable}
                onClose={() => setTurnstileDisableConfirmOpen(false)}
            />
            <Box
                sx={{
                    columns: { xs: 1, md: 2 },
                    columnGap: 3,
                    opacity: isEditable ? 1 : 0.6,
                    pointerEvents: isEditable ? 'auto' : 'none',
                }}
            >
                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('resources.campaign.name', { smart_count: 2 })}
                        </Typography>
                        <TextField
                            type="email"
                            label={translate('hallinta.settings.campaign_csv_email')}
                            value={campaignEmail}
                            onChange={event => setCampaignEmail(event.target.value)}
                            fullWidth
                            disabled={saving}
                        />
                        <Button
                            onClick={() => withSave({ sample_books_csv_email: campaignEmail })}
                            disabled={saving}
                        >
                            {translate('ra.action.save')}
                        </Button>
                    </CardContent>
                </Card>
                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('hallinta.settings.turnstile_title')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={turnstileEnabled}
                                    disabled={saving}
                                    onChange={e => {
                                        if (!e.target.checked) {
                                            setTurnstileDisableConfirmOpen(
                                                true
                                            );
                                            return;
                                        }
                                        handleToggleTurnstile(true);
                                    }}
                                />
                            }
                            label={translate(
                                'hallinta.settings.turnstile_enabled'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            {translate(
                                'hallinta.settings.turnstile_enabled_help'
                            )}
                        </Typography>
                        {envTab === 'dev' && (
                            <>
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    label={translate(
                                        'hallinta.settings.turnstile_dev_scenario'
                                    )}
                                    value={turnstileDevScenario}
                                    disabled={saving}
                                    onChange={e =>
                                        handleSaveTurnstileDevScenario(
                                            e.target.value
                                        )
                                    }
                                >
                                    {TURNSTILE_DEV_SCENARIOS.map(scenario => (
                                        <MenuItem
                                            key={scenario}
                                            value={scenario}
                                        >
                                            {translate(
                                                `hallinta.settings.turnstile_scenario_${scenario}`
                                            )}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {turnstileDevScenario !== 'normal' && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        {translate(
                                            'hallinta.settings.turnstile_dev_warning'
                                        )}
                                    </Alert>
                                )}
                            </>
                        )}
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2 }}
                        >
                            {translate(
                                'hallinta.settings.turnstile_credentials_help'
                            )}
                        </Typography>
                        {canReadSecrets && canWriteSecrets && (
                            <>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ mt: 2 }}
                                    disabled={saving || envTab !== currentEnv}
                                    onClick={() => setTurnstileKeysOpen(true)}
                                >
                                    {translate('hallinta.settings.turnstile_keys_edit')}
                                </Button>
                                {envTab !== currentEnv && (
                                    <Typography
                                        variant="caption"
                                        display="block"
                                        sx={{ mt: 1 }}
                                    >
                                        {translate(
                                            'hallinta.settings.turnstile_keys_current_env'
                                        )}
                                    </Typography>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('hallinta.settings.diagnostic_title')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={globalDiagnosticEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleGlobalDiagnostic(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.diagnostic_global_enabled'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            {translate(
                                'hallinta.settings.diagnostic_global_enabled_help'
                            )}
                        </Typography>
                        <Tabs
                            value={diagnosticBackend}
                            onChange={(_, backend) =>
                                handleDiagnosticBackendChange(backend)
                            }
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                mb: 2,
                                pointerEvents: 'auto',
                                minHeight: 36,
                                '& .MuiTabs-indicator': { height: 2 },
                                '& .MuiTab-root': {
                                    minHeight: 36,
                                    minWidth: 0,
                                    px: 1,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                },
                            }}
                        >
                            {DIAGNOSTIC_BACKENDS.map(backend => (
                                <Tab
                                    key={backend}
                                    value={backend}
                                    disabled={saving}
                                    label={
                                        <Stack
                                            direction="row"
                                            spacing={0.5}
                                            alignItems="center"
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    bgcolor:
                                                        diagnosticBackends[
                                                            backend
                                                        ]?.enabled === true
                                                            ? 'success.main'
                                                            : 'action.disabled',
                                                }}
                                            />
                                            <span>
                                                {diagnosticBackendLabel(
                                                    backend
                                                )}
                                            </span>
                                        </Stack>
                                    }
                                />
                            ))}
                        </Tabs>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={diagnosticEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        setDiagnosticEnabled(e.target.checked)
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.diagnostic_enabled'
                            )}
                        />
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label={translate(
                                'hallinta.settings.diagnostic_detail'
                            )}
                            value={diagnosticDetail}
                            disabled={saving}
                            onChange={e => setDiagnosticDetail(e.target.value)}
                            sx={{ mt: 2 }}
                        >
                            <MenuItem value="basic">basic</MenuItem>
                            <MenuItem value="diagnostic">diagnostic</MenuItem>
                            <MenuItem value="verbose">verbose</MenuItem>
                        </TextField>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                `hallinta.settings.diagnostic_detail_${diagnosticDetail}`
                            )}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ mt: 2 }}>
                            {translate('hallinta.settings.diagnostic_paths')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={diagnosticAllPathsSelected}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleDiagnosticAllPaths(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.diagnostic_all_paths'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                        >
                            {translate(
                                'hallinta.settings.diagnostic_all_paths_help'
                            )}
                        </Typography>
                        <Box
                            sx={{
                                mt: 1,
                                maxHeight: 260,
                                overflowY: 'auto',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1,
                                opacity: diagnosticAllPathsSelected ? 0.5 : 1,
                                pointerEvents: diagnosticAllPathsSelected
                                    ? 'none'
                                    : 'auto',
                            }}
                        >
                            {(diagnosticRoutes[diagnosticBackend] || []).map(
                                route => {
                                    const routeKey = `${route.method} ${route.path}`;
                                    return (
                                        <FormControlLabel
                                            key={routeKey}
                                            sx={{
                                                display: 'flex',
                                                my: 0,
                                                minHeight: 28,
                                                '& .MuiFormControlLabel-label':
                                                    {
                                                        fontSize: '0.78rem',
                                                        lineHeight: 1.2,
                                                    },
                                            }}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    sx={{ p: 0.5 }}
                                                    checked={diagnosticPaths
                                                        .split('\n')
                                                        .some(
                                                            selectedPath =>
                                                                selectedPath ===
                                                                    routeKey ||
                                                                selectedPath ===
                                                                    route.path
                                                        )}
                                                    disabled={
                                                        saving ||
                                                        diagnosticAllPathsSelected
                                                    }
                                                    onChange={e =>
                                                        handleDiagnosticPathChange(
                                                            routeKey,
                                                            e.target.checked,
                                                            route.path
                                                        )
                                                    }
                                                />
                                            }
                                            label={`${route.method} ${route.path}`}
                                        />
                                    );
                                }
                            )}
                        </Box>
                        <TextField
                            type="datetime-local"
                            size="small"
                            fullWidth
                            label={translate(
                                'hallinta.settings.diagnostic_expires'
                            )}
                            value={diagnosticExpiresAt}
                            disabled={saving}
                            onChange={e =>
                                setDiagnosticExpiresAt(e.target.value)
                            }
                            InputLabelProps={{ shrink: true }}
                            sx={{ mt: 2 }}
                        />
                        <Button
                            variant="outlined"
                            disabled={saving}
                            onClick={handleSaveDiagnostic}
                            sx={{ mt: 2 }}
                        >
                            {translate('hallinta.settings.save')}
                        </Button>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate('hallinta.settings.diagnostic_help')}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('hallinta.settings.title')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={sessionsEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleSessions(e.target.checked)
                                    }
                                />
                            }
                            label={translate('hallinta.settings.list_sessions')}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate('hallinta.settings.list_sessions_help')}
                        </Typography>

                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={staleInfoEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleStaleInfo(e.target.checked)
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.list_stale_info'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.list_stale_info_help'
                            )}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate(
                                'hallinta.settings.user_data_recheck_title'
                            )}
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                        >
                            <TextField
                                type="number"
                                size="small"
                                value={userDataRecheckDaysInput}
                                disabled={saving}
                                onChange={e =>
                                    setUserDataRecheckDaysInput(e.target.value)
                                }
                                inputProps={{ min: 1, max: 3650 }}
                                sx={{ width: 140 }}
                            />
                            <Button
                                variant="outlined"
                                disabled={
                                    saving ||
                                    Number(userDataRecheckDaysInput) ===
                                        userDataRecheckDays
                                }
                                onClick={handleSaveUserDataRecheckDays}
                            >
                                {translate('hallinta.settings.save')}
                            </Button>
                        </Stack>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.user_data_recheck_help'
                            )}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('hallinta.settings.security_title')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={basicLoginEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleBasicLogin(e.target.checked)
                                    }
                                />
                            }
                            label={translate('hallinta.settings.basic_login')}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate('hallinta.settings.basic_login_help')}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('hallinta.settings.invitation_title')}
                        </Typography>

                        <Typography variant="subtitle1" gutterBottom>
                            {translate('hallinta.settings.invitation_ttl')}
                        </Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                        >
                            <TextField
                                type="number"
                                size="small"
                                value={ttlInput}
                                disabled={saving}
                                onChange={e => setTtlInput(e.target.value)}
                                inputProps={{ min: 1, max: 365 }}
                                sx={{ width: 140 }}
                            />
                            <Button
                                variant="outlined"
                                disabled={
                                    saving || Number(ttlInput) === ttlDays
                                }
                                onClick={handleSaveTtl}
                            >
                                {translate('hallinta.settings.save')}
                            </Button>
                        </Stack>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate('hallinta.settings.invitation_ttl_help')}
                        </Typography>

                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={ttlEnforced}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleEnforced(e.target.checked)
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.invitation_ttl_enforced'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.invitation_ttl_enforced_help'
                            )}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            {translate('hallinta.settings.auto_resend')}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={autoEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleAuto(e.target.checked)
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.auto_resend_enabled'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, mb: 2 }}
                        >
                            {translate(
                                'hallinta.settings.auto_resend_enabled_help'
                            )}
                        </Typography>

                        <TextField
                            select
                            size="small"
                            label={translate(
                                'hallinta.settings.auto_resend_mode'
                            )}
                            value={autoMode}
                            disabled={saving}
                            onChange={e => handleChangeMode(e.target.value)}
                            sx={{ width: 320, mb: 2 }}
                        >
                            <MenuItem value="all">
                                {translate(
                                    'hallinta.settings.auto_resend_mode_all'
                                )}
                            </MenuItem>
                            <MenuItem value="stuck_failed">
                                {translate(
                                    'hallinta.settings.auto_resend_mode_stuck_failed'
                                )}
                            </MenuItem>
                        </TextField>

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                            sx={{ mb: 1 }}
                        >
                            <TextField
                                size="small"
                                label={translate(
                                    'hallinta.settings.auto_resend_schedule'
                                )}
                                value={scheduleInput}
                                disabled={saving}
                                onChange={e => setScheduleInput(e.target.value)}
                                sx={{ width: 240 }}
                            />
                            <Button
                                variant="outlined"
                                disabled={
                                    saving ||
                                    scheduleInput.trim() ===
                                        scheduleSaved.join(', ')
                                }
                                onClick={handleSaveSchedule}
                            >
                                {translate('hallinta.settings.save')}
                            </Button>
                        </Stack>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                        >
                            {translate(
                                'hallinta.settings.auto_resend_schedule_help'
                            )}
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ breakInside: 'avoid', mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('hallinta.settings.license_cache_title')}
                        </Typography>

                        <Typography variant="subtitle1" gutterBottom>
                            {translate(
                                'hallinta.settings.license_cache_subtitle'
                            )}
                        </Typography>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={licenseDataCacheEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleLicenseDataCache(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.license_data_cache_enabled'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, mb: 2 }}
                        >
                            {translate(
                                'hallinta.settings.license_data_cache_enabled_help'
                            )}
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                        >
                            <TextField
                                type="number"
                                size="small"
                                label={translate(
                                    'hallinta.settings.license_cache_ttl'
                                )}
                                value={licenseCacheTtlInput}
                                disabled={saving || !licenseDataCacheEnabled}
                                onChange={e =>
                                    setLicenseCacheTtlInput(e.target.value)
                                }
                                inputProps={{ min: 0, max: 3600 }}
                                sx={{ width: 160 }}
                            />
                            <Button
                                variant="outlined"
                                disabled={
                                    saving ||
                                    !licenseDataCacheEnabled ||
                                    Number(licenseCacheTtlInput) ===
                                        licenseCacheTtl
                                }
                                onClick={handleSaveLicenseCacheTtl}
                            >
                                {translate('hallinta.settings.save')}
                            </Button>
                        </Stack>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.license_cache_ttl_help'
                            )}
                        </Typography>

                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={licenseCachePrewarmEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleLicensePrewarm(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.license_cache_prewarm'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.license_cache_prewarm_help'
                            )}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            {translate(
                                'hallinta.settings.teacher_cache_subtitle'
                            )}
                        </Typography>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={teacherDataCacheEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleTeacherDataCache(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.teacher_data_cache_enabled'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, mb: 2 }}
                        >
                            {translate(
                                'hallinta.settings.teacher_data_cache_enabled_help'
                            )}
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                        >
                            <TextField
                                type="number"
                                size="small"
                                label={translate(
                                    'hallinta.settings.teacher_cache_ttl'
                                )}
                                value={teacherCacheTtlInput}
                                disabled={saving || !teacherDataCacheEnabled}
                                onChange={e =>
                                    setTeacherCacheTtlInput(e.target.value)
                                }
                                inputProps={{ min: 0, max: 604800 }}
                                sx={{ width: 160 }}
                            />
                            <Button
                                variant="outlined"
                                disabled={
                                    saving ||
                                    !teacherDataCacheEnabled ||
                                    Number(teacherCacheTtlInput) ===
                                        teacherCacheTtl
                                }
                                onClick={handleSaveTeacherCacheTtl}
                            >
                                {translate('hallinta.settings.save')}
                            </Button>
                        </Stack>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.teacher_cache_ttl_help'
                            )}
                        </Typography>

                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={teacherCachePrewarmEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleTeacherPrewarm(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.teacher_cache_prewarm'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.teacher_cache_prewarm_help'
                            )}
                        </Typography>

                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={teacherCacheInvalidateOnEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleTeacherInvalidateOn(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.teacher_cache_invalidate_on'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.teacher_cache_invalidate_on_help'
                            )}
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" gutterBottom>
                            {translate(
                                'hallinta.settings.product_mem_cache_subtitle'
                            )}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={productMemCacheBroadcastEnabled}
                                    disabled={saving}
                                    onChange={e =>
                                        handleToggleProductMemCacheBroadcast(
                                            e.target.checked
                                        )
                                    }
                                />
                            }
                            label={translate(
                                'hallinta.settings.product_mem_cache_broadcast_enabled'
                            )}
                        />
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                        >
                            {translate(
                                'hallinta.settings.product_mem_cache_broadcast_enabled_help'
                            )}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};

export default SettingsPanel;
